import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Globe, RefreshCw, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

const PreviewPanel = ({ project }) => {
  const [deployUrl, setDeployUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchDeployStatus = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/deploy/${project.id}/status`);
      if (res.data.has_deployment && res.data.status === 'ready' && res.data.url) {
        setDeployUrl(res.data.url);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [project?.id]);

  useEffect(() => { fetchDeployStatus(); }, [fetchDeployStatus]);

  const previewUrl = project?.previewUrl || deployUrl;

  const handleCopy = () => {
    if (previewUrl) {
      navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="bg-card border-b border-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-400" />
            <h3 className="text-foreground font-semibold text-sm" data-testid="preview-panel-title">Live Preview</h3>
            {previewUrl && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live
              </span>
            )}
          </div>
          <button onClick={fetchDeployStatus} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="refresh-preview-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 p-5 flex items-center justify-center">
        {loading ? (
          <div className="text-center">
            <RefreshCw className="w-8 h-8 text-blue-400/50 animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Checking deployment...</p>
          </div>
        ) : previewUrl ? (
          <div className="w-full max-w-sm text-center space-y-5">
            {/* Live status card */}
            <div className="p-5 bg-muted rounded-xl border border-emerald-500/20">
              <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                <Globe className="w-7 h-7 text-emerald-400" />
              </div>
              <h3 className="text-foreground font-semibold mb-1">Your app is live</h3>
              <p className="text-xs text-muted-foreground mb-4">Deployed on Vercel Edge Network</p>
              
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-all text-sm"
                data-testid="open-preview-newtab"
              >
                <ExternalLink className="w-4 h-4" />
                Open Preview
              </a>
            </div>

            {/* URL display */}
            <div className="flex items-center gap-2 px-3 py-2.5 bg-muted rounded-lg border border-border">
              <Globe className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span className="text-xs text-muted-foreground font-mono truncate flex-1 text-left" data-testid="preview-url">{previewUrl}</span>
              <button onClick={handleCopy} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" data-testid="copy-preview-url">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600/20 to-violet-500/20 rounded-2xl mx-auto mb-4 flex items-center justify-center border border-blue-500/20">
              <Globe className="w-8 h-8 text-blue-400/50" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-2">No Preview Available</h3>
            <p className="text-sm text-muted-foreground">
              Deploy your project to see a live preview.
            </p>
            <p className="text-xs text-muted-foreground/70 mt-3">
              Click <strong className="text-muted-foreground">Deploy</strong> then <strong className="text-muted-foreground">Quick Deploy</strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewPanel;
